import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { sites } from '@/mocks/sites';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import SiteCard from '@/components/feature/SiteCard';
import FilterPanel from '@/components/feature/FilterPanel';
import {
  defaultFilters,
  filterSites,
  getRegions,
  getScales,
  sortSites,
  sortOptions,
  type SiteFilterState,
} from '@/utils/siteUtils';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const isAll = categoryId === 'all';
  const category = categories.find((item) => item.id === categoryId);

  const [filters, setFilters] = useState<SiteFilterState>({ ...defaultFilters });
  const [sort, setSort] = useState<string>('hot');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const baseSites = useMemo(
    () => (isAll ? sites : sites.filter((site) => site.categoryId === categoryId)),
    [categoryId, isAll],
  );

  const regions = useMemo(() => getRegions(baseSites), [baseSites]);
  const scales = useMemo(() => getScales(baseSites), [baseSites]);

  const result = useMemo(() => sortSites(filterSites(baseSites, filters), sort), [baseSites, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const paged = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setFilters({ ...defaultFilters });
    setSort('hot');
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  if (!category && !isAll) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">该分类可能已调整或暂时不存在，请从首页重新选择。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const title = isAll ? '全部网站' : (category?.name as string);
  const description = isAll
    ? '浏览网址大全收录的全部优质网站资源，支持按地区、规模与更新时间筛选，快速锁定目标站点。'
    : (category?.description as string);

  const handleFilterChange = (next: Partial<SiteFilterState>) => {
    setFilters((prev) => ({ ...prev, ...next }));
  };

  const handleReset = () => setFilters({ ...defaultFilters });

  return (
    <div className="w-full animate-fade-in px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-[1280px]">
        <Breadcrumb items={[{ label: '分类导航', to: '/category/all' }, { label: title }]} />

        <header className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className={`${isAll ? 'ri-apps-line' : category?.icon} text-xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{title}</h1>
              <p className="mt-0.5 text-xs text-foreground-400">共 {result.length} 个网站</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">{description}</p>
        </header>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-4">
              <FilterPanel
                filters={filters}
                regions={regions}
                scales={scales}
                onChange={handleFilterChange}
                onReset={handleReset}
              />
            </div>
          </aside>

          <section>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-600 lg:hidden"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                </button>
                <span className="text-sm text-foreground-500">
                  找到 <strong className="text-foreground-900">{result.length}</strong> 个结果
                </span>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSort(option.value)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === option.value
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {paged.map((site) => (
                    <SiteCard key={site.id} site={site} compact />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="mt-4 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-2-line text-2xl"></i>
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">没有符合条件的网站</h3>
                <p className="mt-1.5 text-sm text-foreground-500">试试调整筛选条件，或重置后浏览全部结果。</p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              </div>
            )}
          </section>
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[80%] overflow-y-auto rounded-t-lg bg-background-50 p-5">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选条件</span>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              regions={regions}
              scales={scales}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50"
            >
              查看结果（{result.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}